import type { RefObject, TouchEvent } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import classNames from 'classnames';

import { UI_ROUTES } from '../../../../util/routes';
import ClickableDiv from '../../../ClickableDiv/ClickableDiv';
import useIsMobile from '../../../hooks/useIsMobile';

import { StockColumnTooltip } from './StockColumnTooltip';

import styles from './StockColumn.module.scss';

type Tooltip = {
  primaryMetric: number,
  secondaryMetric: number,
  marketCap: number,
  volume: number,
};

type TooltipSide = 'left' | 'right';

type TooltipPosition = {
  side: TooltipSide;
  top: number;
  left: number;
  pointerTop: number;
};

type Props = {
  blur: boolean,
  percentageLabel: string,
  value: number,
  maxValue: number,
  stockLogo: string,
  symbol: string,
  isDividend?: boolean,
  isHighGrowth?: boolean,
  tooltip: Tooltip,
  currencySymbol?: string,
  hovered: boolean,
  hoverElement: (symbol: string) => void,
  containerRef: RefObject<HTMLDivElement>,
  animationOrder?: number,
};

const MAX_COLUMN_HEIGHT = 153;
const INITIAL_PIPE_HEIGHT = 0;
const COLUMN_ANIMATION_STAGGER_MS = 50;
const TOUCH_HOVER_DELAY_MS = 150;
const TOUCH_SCROLL_THRESHOLD_PX = 10;
const TOOLTIP_GAP = 8;
const TOOLTIP_EDGE_OFFSET = 8;
const TOOLTIP_POINTER_SIZE = 8;
const DEFAULT_POINTER_TOP = 52;
const DEFAULT_TOOLTIP_POSITION: TooltipPosition = {
  side: 'left',
  top: 0,
  left: 0,
  pointerTop: DEFAULT_POINTER_TOP,
};

const clampValue = ({ value, min, max }: { value: number, min: number, max: number }) => {
  if (max < min) {
    return min;
  }

  if (value < min) {
    return min;
  }

  if (value > max) {
    return max;
  }

  return value;
};

export const StockColumn = ({
  blur,
  percentageLabel,
  value,
  maxValue,
  stockLogo,
  symbol,
  isDividend = false,
  isHighGrowth = false,
  tooltip,
  currencySymbol,
  hovered,
  hoverElement,
  containerRef,
  animationOrder = 0,
}: Props) => {
  const pipeHeight = maxValue ? (value / maxValue) * MAX_COLUMN_HEIGHT : INITIAL_PIPE_HEIGHT;
  const animationDelay = animationOrder * COLUMN_ANIMATION_STAGGER_MS;
  const { isMobile } = useIsMobile();
  const columnRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const touchStartCoordinatesRef = useRef<{ x: number, y: number } | null>(null);
  const touchHoverDelayTimerRef = useRef<number | null>(null);
  const isTouchHoverVisibleRef = useRef(false);
  const [animatedPipeHeight, setAnimatedPipeHeight] = useState(INITIAL_PIPE_HEIGHT);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>(DEFAULT_TOOLTIP_POSITION);

  const navigate = useNavigate();

  const handleRedirect = () => {
    navigate(`${UI_ROUTES.symbol}/${symbol}`);
  };

  const handleMouseEnter = () => {
    if (isMobile) return;
    hoverElement(symbol);
  };

  const handleMouseLeave = () => {
    if (isMobile) return;
    hoverElement('');
  };

  const clearTouchHoverDelayTimer = () => {
    if (touchHoverDelayTimerRef.current === null) {
      return;
    }
    window.clearTimeout(touchHoverDelayTimerRef.current);
    touchHoverDelayTimerRef.current = null;
  };
  const hideTouchHover = () => {
    if (!isTouchHoverVisibleRef.current) {
      return;
    }
    isTouchHoverVisibleRef.current = false;
    hoverElement('');
  };
  const showTouchHover = () => {
    if (isTouchHoverVisibleRef.current) {
      return;
    }
    isTouchHoverVisibleRef.current = true;
    hoverElement(symbol);
  };

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    if (!isMobile) return;
    const touch = event.touches[0];
    if (!touch) return;
    touchStartCoordinatesRef.current = { x: touch.clientX, y: touch.clientY };
    isTouchHoverVisibleRef.current = false;
    clearTouchHoverDelayTimer();
    touchHoverDelayTimerRef.current = window.setTimeout(() => {
      touchHoverDelayTimerRef.current = null;
      showTouchHover();
    }, TOUCH_HOVER_DELAY_MS);
  };

  const handleTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    if (!isMobile) return;

    const touchStartCoordinates = touchStartCoordinatesRef.current;
    const touch = event.touches[0];

    if (!touchStartCoordinates || !touch) {
      return;
    }

    const movedX = Math.abs(touch.clientX - touchStartCoordinates.x);
    const movedY = Math.abs(touch.clientY - touchStartCoordinates.y);

    if (movedY > TOUCH_SCROLL_THRESHOLD_PX && movedY > movedX) {
      clearTouchHoverDelayTimer();
      hideTouchHover();
      return;
    }

    if (movedX > TOUCH_SCROLL_THRESHOLD_PX && movedX > movedY) {
      clearTouchHoverDelayTimer();
      showTouchHover();
    }
  };

  const handleTouchEnd = () => {
    if (!isMobile) return;
    touchStartCoordinatesRef.current = null;
    clearTouchHoverDelayTimer();
    hideTouchHover();
  };

  const handleTouchCancel = handleTouchEnd;

  const renderTooltip = () => {
    const { primaryMetric, secondaryMetric, volume, marketCap } = tooltip;

    return (
      <StockColumnTooltip
        primaryMetric={primaryMetric}
        secondaryMetric={secondaryMetric}
        volume={volume}
        marketCap={marketCap}
        isDividend={isDividend}
        currencySymbol={currencySymbol}
        visible={hovered}
        side={tooltipPosition.side}
        top={tooltipPosition.top}
        left={tooltipPosition.left}
        pointerTop={tooltipPosition.pointerTop}
        tooltipRef={tooltipRef}
      />
    );
  };

  useEffect(() => {
    setAnimatedPipeHeight(INITIAL_PIPE_HEIGHT);
    const animationFrameId = window.requestAnimationFrame(() => {
      setAnimatedPipeHeight(pipeHeight);
    });

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [pipeHeight]);

  useEffect(() => () => {
    clearTouchHoverDelayTimer();
  }, []);

  useLayoutEffect(() => {
    if (!hovered || isMobile) {
      return undefined;
    }

    const updateTooltipPosition = () => {
      const containerElement = containerRef.current;
      const columnElement = columnRef.current;
      const tooltipElement = tooltipRef.current;

      if (!containerElement || !columnElement || !tooltipElement) {
        return;
      }

      const containerRect = containerElement.getBoundingClientRect();
      const columnRect = columnElement.getBoundingClientRect();
      const tooltipRect = tooltipElement.getBoundingClientRect();

      const columnCenterX = columnRect.left + (columnRect.width / 2);
      const columnCenterY = columnRect.top + (columnRect.height / 2);
      const rightSpace = containerRect.right - columnRect.right - TOOLTIP_GAP;
      const leftSpace = columnRect.left - containerRect.left - TOOLTIP_GAP;

      let shouldShowOnLeft = false;

      if (leftSpace >= tooltipRect.width && rightSpace < tooltipRect.width) {
        shouldShowOnLeft = true;
      } else if (leftSpace < tooltipRect.width && rightSpace >= tooltipRect.width) {
        shouldShowOnLeft = false;
      } else {
        shouldShowOnLeft = leftSpace > rightSpace;
      }

      const initialLeft = shouldShowOnLeft
        ? columnRect.left - tooltipRect.width - TOOLTIP_GAP
        : columnRect.right + TOOLTIP_GAP;
      const minLeft = containerRect.left + TOOLTIP_EDGE_OFFSET;
      const maxLeft = containerRect.right - tooltipRect.width - TOOLTIP_EDGE_OFFSET;
      const clampedLeft = clampValue({ value: initialLeft, min: minLeft, max: maxLeft });
      const initialTop = columnCenterY - (tooltipRect.height / 2);
      const minTop = containerRect.top + TOOLTIP_EDGE_OFFSET;
      const maxTop = containerRect.bottom - tooltipRect.height - TOOLTIP_EDGE_OFFSET;
      const clampedTop = clampValue({ value: initialTop, min: minTop, max: maxTop });
      const tooltipCenterX = clampedLeft + (tooltipRect.width / 2);
      const side: TooltipSide = columnCenterX <= tooltipCenterX ? 'left' : 'right';
      const pointerOffset = TOOLTIP_POINTER_SIZE + TOOLTIP_EDGE_OFFSET;
      const pointerTop = clampValue({
        value: columnCenterY - clampedTop,
        min: pointerOffset,
        max: tooltipRect.height - pointerOffset,
      });

      setTooltipPosition({
        side,
        left: clampedLeft - columnRect.left,
        top: clampedTop - columnRect.top,
        pointerTop,
      });
    };

    updateTooltipPosition();
    window.addEventListener('resize', updateTooltipPosition);
    window.addEventListener('scroll', updateTooltipPosition, true);

    return () => {
      window.removeEventListener('resize', updateTooltipPosition);
      window.removeEventListener('scroll', updateTooltipPosition, true);
    };
  }, [containerRef, hovered, isMobile]);

  return (
    <ClickableDiv
      ref={columnRef}
      className={classNames(styles.wrapper, {[styles.blured]: blur, [styles.hovered]: hovered})}
      onClick={handleRedirect}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
    >
      <div className={styles.contentWrapper}>
        <p className={styles.percentage}>{percentageLabel}%</p>
        <span
          className={
            classNames(
              styles.coloredPipe,
              {[styles.blue]: isHighGrowth, [styles.green]: isDividend, [styles.mobileColumn]: isMobile})
          }
          style={{
            height: `${animatedPipeHeight}px`,
            transitionDelay: `0ms, 0ms, ${animationDelay}ms`,
          }}
        />
        <div className={styles.symbolWrapper}>
          <img className={styles.logo} src={stockLogo} alt={symbol} />
          <h3 className={styles.symbol}>{symbol}</h3>
        </div>
      </div>
      {!isMobile && renderTooltip()}
    </ClickableDiv>
  );
};
